<template>
    <v-dialog v-model="modelValue" max-width="1000px">
        <v-card>
            <v-card-title>
                {{ $t('Требуется Ваша подпись') }}
            </v-card-title>

            <v-container v-if="!isLoading">
                <div id="main">
                    <div id="inner">
                        <vue-pdf-embed v-if="pdfSource" :source="pdfSource" />
                    </div>
                </div>
            </v-container>

            <v-form ref="form" @submit.prevent="save">
                <v-container>
                    <v-row>
                        <v-col>Способ подписания:</v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-radio-group row v-model="selectedSignMethod">
                                <v-radio
                                    color="gray"
                                    v-for="signMethod in availableSignMethods"
                                    :key="signMethod.value"
                                    :label="signMethod.label"
                                    :value="signMethod.value"
                                />
                            </v-radio-group>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>

            <v-container>
                <v-row>
                    <v-col>
                        <EDSSign v-if="modelValue && selectedSignMethod === 'eds'" :document="base64document" @getEDSData="getEDSData" />
                        <QRCodeSign
                            v-if="modelValue && selectedSignMethod === 'qrCode' && base64document"
                            ref="qrSigner"
                            :document="base64document"
                            :file_name="'Предписание'"
                            @getQRData="getQRData"
                        />
                    </v-col>
                </v-row>
            </v-container>
            <v-card-actions class="justify-end">
                <v-btn text @click="onCancel">
                    {{ $t('Отмена') }}
                </v-btn>
                <v-btn color="gray" class="white--text" :disabled="!canSave" @click="save">
                    {{ $t('Сохранить') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';
import QRCodeSign from '@/components/sign_document/QRCodeSignOZP.vue';
import EDSSign from '@/components/sign_document/EDSSignOZP.vue';
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import { base64ToObjectURL } from '@/utils';
import PrescriptionOZPService from '@/services/apiRequests/prescription_ozp.service';
import { base64ToFile } from '@/utils/base64ToFile';

const SignTypes = {
    EDS: 'eds',
    QRCode: 'qrCode'
};

export default {
    name: 'SignPrescription',
    components: {
        QRCodeSign,
        EDSSign,
        VuePdfEmbed
    },
    props: {
        modelValue: {
            type: Boolean,
            required: true
        },
        prescription: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['update:modelValue', 'saved'],
    data() {
        return {
            pdfKey: 0,
            zoom: 1.5,
            signData: null,
            base64document: null,
            isLoading: false,
            pdfUrl: null,
            selectedSignMethod: SignTypes.QRCode,
            signMethods: [
                { label: 'С помощью ЭЦП', value: SignTypes.EDS },
                { label: 'QR code', value: SignTypes.QRCode }
            ]
        };
    },
    computed: {
        pdfSource() {
            return this.pdfUrl || '';
        },
        availableSignMethods() {
            return this.signMethods;
        },
        canSave() {
            return !!this.signData;
        }
    },
    watch: {
        modelValue(val) {
            if (!val) {
                this.$refs.qrSigner?.cleanup?.();
            }
            if (val) {
                this.refreshDoc();
            }
        }
    },
    beforeDestroy() {
        this.$refs.qrSigner?.cleanup?.();
    },
    methods: {
        async refreshDoc() {
            this.isLoading = true;
            this.base64document = null;
            this.pdfUrl = null;
            try {
                const resp = await PrescriptionOZPService.getPrescriptionInfoToSign(this.prescription.record_id);
                this.base64document = resp.document_to_sign;
                this.pdfUrl = base64ToObjectURL(this.base64document);
            } catch (e) {
                console.error('Ошибка при получении документа на подпись:', e);
                this.$toast?.error?.(this.$t('Не удалось получить документ на подпись'));
            } finally {
                this.isLoading = false;
            }
        },
        onCancel() {
            this.$refs.qrSigner?.cleanup?.();
            this.$emit('update:modelValue', false);
        },
        zoomIn() {
            this.zoom = Math.min(this.zoom + 0.1, 3);
            this.pdfKey++;
        },
        zoomOut() {
            this.zoom = Math.max(this.zoom - 0.1, 0.5);
            this.pdfKey++;
        },
        getQRData(qrData) {
            this.signData = qrData;
        },
        getEDSData(edsData) {
            this.signData = edsData;
        },
        async getData() {
            this.isLoading = true;
            try {
                const response = await PrescriptionOZPService.getPrescriptionInfoToSign(this.prescription.record_id);
                this.base64document = response.document_to_sign;
                this.pdfUrl = base64ToObjectURL(this.base64document);
            } catch (e) {
                console.error('Ошибка при получении документа на подпись:', e);
            } finally {
                this.isLoading = false;
            }
        },
        async save() {
            try {
                if (!this.signData) {
                    this.$toast?.error?.(this.$t('Документ не подписан'));
                    return;
                }

                const signedBase64 = this.signData;

                const fileObj = base64ToFile(signedBase64, 'Предписание.pdf', 'application/pdf');

                //const fileObj = base64ToFile(this.base64document, 'prescription.pdf', 'application/pdf');

                const formData = new FormData();

                if (fileObj instanceof Blob && !(fileObj instanceof File)) {
                    formData.append('file', fileObj, 'prescription.pdf');
                } else {
                    formData.append('file', fileObj);
                }
                formData.append('guid', this.prescription.guid);
                formData.append('document', 'prescription');

                await PrescriptionOZPService.saveFilePrescription({ file: fileObj, guid: this.prescription.guid });
                await PrescriptionOZPService.prescriptionOZPStatusChange(this.prescription.record_id, 2);
                this.$emit('saved');
                this.$emit('update:modelValue', false);
            } catch (e) {
                console.error('Ошибка при сохранении файла:', e);
                this.$toast?.error?.('Не удалось сохранить файл');
            }
        }
    }
};
</script>

<style scoped>
#main {
    display: flex;
    justify-content: center;
}

#inner {
    border: 2px dashed #aaa;
    padding: 10px;
    background-color: #f9f9f9;
    max-width: 1200px;
    width: 100%;
}

.zoom-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    gap: 10px;
}

.zoom-label {
    font-weight: bold;
    width: 50px;
    text-align: center;
}
</style>
